import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { EnhancedAbsenceModel } from '@app/reducers/orm/absence/absence.model';
import { EMPLOYEE_POPOVER_PLACEMENT } from '@app/shared/sb-lib/employee-popover-placement.token';
import { EmployeeModel } from '@reducers/orm/employee/employee.model';
import { AvatarComponent } from '@sb/ui/avatar';
import { BadgeIconComponent } from '@sb/ui/badge-icon';
import type { Icon } from '@sb/ui/icon';
import { PopoverDirective, PopoverModule } from '@sb/ui/popover';
import { clsx } from 'clsx';

import { AbsenceBadgeIconDirective } from '../../absence/absence-badge-icon.directive';

export enum Orientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum ItemType {
  ICON = 'icon',
  ABSENCE_BADGE = 'absence-badge',
  AVATAR = 'avatar',
  MORE_INDICATOR = 'more-indicator',
}

// TODO for now this thing will live here instead of ui-lib because of the above imports
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-avatar-badge-group',
  imports: [CommonModule, BadgeIconComponent, AbsenceBadgeIconDirective, AvatarComponent, PopoverModule],
  templateUrl: './avatar-badge-group.component.html',
})
export class AvatarBadgeGroupComponent implements OnChanges {
  ItemType = ItemType;

  popoverPosition = inject(EMPLOYEE_POPOVER_PLACEMENT);

  @ViewChild(PopoverDirective)
  popover: PopoverDirective;

  items = [];
  _employees: EmployeeModel[] = [];
  employeeMoreIndicator: { type: ItemType; count: number; classes: string } | null = null;

  @Input()
  icons: Icon[] = [];

  @Input()
  absences: EnhancedAbsenceModel[] = [];

  @Input()
  employees: EmployeeModel[] = [];

  @Input()
  truncateEmployeesAfter: number;

  @Input()
  orientation: Orientation = Orientation.VERTICAL;

  @Input()
  popoverTemplate: TemplateRef<unknown>;

  @HostBinding('class')
  get classes() {
    return clsx([
      'flex items-center justify-center',
      this.orientation === Orientation.HORIZONTAL ? 'flex-row-reverse pl-1' : 'flex-col-reverse pt-1',
    ]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      this.truncateEmployeesAfter &&
      this.truncateEmployeesAfter > 0 &&
      this.employees.length > this.truncateEmployeesAfter &&
      changes['employees']
    ) {
      this._employees = this.employees.slice(0, this.truncateEmployeesAfter);
      this.employeeMoreIndicator = {
        type: ItemType.MORE_INDICATOR,
        count: this.employees.length - this.truncateEmployeesAfter,
        classes: '',
      };
    } else {
      this._employees = this.employees;
      this.employeeMoreIndicator = null;
    }

    if (changes['icons'] || changes['employees'] || changes['absenceOptions']) {
      this.items = this.getItems();
    }
  }

  private getItems() {
    const items = [];
    if (this.icons.length > 0) {
      items.push(...this.icons.map((icon) => ({ type: ItemType.ICON, icon, classes: '' })).reverse());
    }
    if (this.absences.length > 0) {
      items.push(...this.absences.map((absence) => ({ type: ItemType.ABSENCE_BADGE, absence, classes: '' })).reverse());
    }
    if (this.employeeMoreIndicator) {
      items.push(this.employeeMoreIndicator);
    }
    if (this._employees.length > 0) {
      items.push(...this._employees.map((employee) => ({ type: ItemType.AVATAR, employee, classes: '' })).reverse());
    }

    items.forEach((item) => {
      item.classes = this.getItemClasses(item);
    });

    return items;
  }

  private getItemClasses(item: { type: ItemType; absence?: EnhancedAbsenceModel }) {
    return clsx([
      'box-content border-2 border-solid',
      this.orientation === Orientation.HORIZONTAL ? '-ml-1' : '-mt-1',
      item.type === ItemType.ABSENCE_BADGE && item.absence?.status === 'Pending' ? '' : 'border-default',
    ]);
  }
}
